/**
 * Google's file picker, which is how a plan somebody shared gets opened.
 *
 * This is not a convenience. `drive.file` — the only Drive scope this app asks
 * for — grants access to the files a person created with the app and the files
 * they *picked* with it, and nothing else. So a scenario shared with somebody is
 * invisible to the app until they choose it here: it will not appear in their
 * list, and a file id typed in by hand comes back 404. Picking is the act that
 * turns a Drive share into access, which is also why it cannot be replaced with
 * a box to paste a link into.
 *
 * The alternative was `drive.readonly`, which would let the app enumerate
 * everything in everybody's Drive. That is a great deal of access to buy one
 * dialog.
 *
 * **`setAppId` is what makes any of that true.** Without the Cloud project
 * number, the picker is only a file browser: somebody chooses a scenario, the
 * dialog closes as if it worked, and the app is then refused the file it was
 * just handed — "not shared with this app", which is a confusing thing to be
 * told about a file you can see in your own Drive. The pick is what grants the
 * `drive.file` access, and the app id is how Google knows which app to grant
 * it to.
 */
import { SCENARIO_MIME } from '@tive/core';

const SCRIPT = 'https://apis.google.com/js/api.js';

/** The bits of `gapi` and the picker this file uses. */
interface PickerDoc {
  id?: string;
  name?: string;
}

interface PickerResponse {
  action?: string;
  docs?: PickerDoc[];
}

interface PickerView {
  setMimeTypes(types: string): PickerView;
  setQuery(query: string): PickerView;
  setIncludeFolders(include: boolean): PickerView;
  setSelectFolderEnabled(enabled: boolean): PickerView;
  /** False for the "Shared with me" half of Drive, which is the point here. */
  setOwnedByMe(owned: boolean): PickerView;
  setLabel(label: string): PickerView;
}

interface PickerBuilder {
  addView(view: PickerView): PickerBuilder;
  setOAuthToken(token: string): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  /** The Cloud project *number*. See the note at the top of this file. */
  setAppId(appId: string): PickerBuilder;
  setTitle(title: string): PickerBuilder;
  setCallback(callback: (response: PickerResponse) => void): PickerBuilder;
  build(): { setVisible(visible: boolean): void };
}

interface GooglePicker {
  picker: {
    PickerBuilder: new () => PickerBuilder;
    DocsView: new (viewId?: unknown) => PickerView;
    ViewId: { DOCS: unknown };
    Action: { PICKED: string };
  };
}

interface Gapi {
  load(component: string, callback: () => void): void;
}

declare global {
  interface Window {
    gapi?: Gapi;
  }
}

let loading: Promise<GooglePicker> | null = null;

/**
 * Load Google's picker, once.
 *
 * Deliberately not bundled: the picker is Google's own dialog against Google's
 * own Drive, and a vendored copy would be a copy of something that has to match
 * whatever Drive is doing today. The sign-in flow already loads from
 * accounts.google.com for the same reason.
 */
export function loadPicker(): Promise<GooglePicker> {
  loading ??= new Promise<GooglePicker>((resolve, reject) => {
    const ready = () => {
      const gapi = window.gapi;
      if (!gapi) {
        reject(new Error('Google’s picker did not load.'));
        return;
      }
      gapi.load('picker', () => {
        const google = (window as unknown as { google?: GooglePicker }).google;
        if (google?.picker) resolve(google);
        else reject(new Error('Google’s picker did not load.'));
      });
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT}"]`);
    if (existing) {
      if (window.gapi) ready();
      else existing.addEventListener('load', ready, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', ready, { once: true });
    script.addEventListener('error', () => reject(new Error('Google’s picker could not be reached.')), {
      once: true,
    });
    document.head.appendChild(script);
  });

  // A failed load must not be remembered, or the button never works again.
  return loading.catch((error: unknown) => {
    loading = null;
    throw error instanceof Error ? error : new Error(String(error));
  });
}

/**
 * Show the picker and resolve with the file the person chose, or null if they
 * closed it.
 *
 * Filtered so it only offers scenarios. Both MIME types are listed because a
 * plan saved before the app had a type of its own is still `application/json`,
 * and the name query is what keeps that half of it from being every JSON file
 * in the person's Drive.
 */
export async function pickScenarioFile(options: {
  apiKey: string;
  /** Cloud project number. Without it a pick grants the app nothing. */
  appId: string;
  driveToken: string;
}): Promise<{ id: string; name: string } | null> {
  const google = await loadPicker();

  return new Promise((resolve) => {
    const scenarios = (owned: boolean) =>
      new google.picker.DocsView(google.picker.ViewId.DOCS)
        .setMimeTypes(`${SCENARIO_MIME},application/json`)
        .setQuery('tive-scenario')
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setOwnedByMe(owned)
        .setLabel(owned ? 'My scenarios' : 'Shared with me');

    /*
     * Two tabs, and the second is the one that matters: a plan somebody
     * shared is not in the person's own Drive, so the default view does not
     * list it — which looks like the share never arrived.
     */
    new google.picker.PickerBuilder()
      .addView(scenarios(false))
      .addView(scenarios(true))
      .setOAuthToken(options.driveToken)
      .setDeveloperKey(options.apiKey)
      .setAppId(options.appId)
      .setTitle('Open a scenario')
      .setCallback((response) => {
        if (response.action !== google.picker.Action.PICKED) {
          // Cancelled, or one of the picker's other lifecycle notices. Either
          // way there is nothing to open, and nothing to report.
          if (response.action && response.action !== 'loaded') resolve(null);
          return;
        }
        const [doc] = response.docs ?? [];
        resolve(doc?.id ? { id: doc.id, name: doc.name ?? 'Shared scenario' } : null);
      })
      .build()
      .setVisible(true);
  });
}
